import type { SummaryMetrics } from '../../../../../../apis/graphql';
import { formatNumber } from '../../../../../../utils/formatting';
import { CUSTOM_PACING_INFO_TOOLTIPS, SUMMARY_TABLE_LABELS } from '../../constants';
import type { SummaryTableData, SummaryTableHeader } from './SummaryTable/SummaryTable';

type PacingImpressionDataPreview = {
  pacingImpressionHeaders: SummaryTableHeader[];
  pacingImpressionData: SummaryTableData[][];
};

export const mapPacingImpressionToPreview = (summary: SummaryMetrics): PacingImpressionDataPreview => {
  const pacingImpressionHeaders: SummaryTableHeader[] = [
    {
      label: SUMMARY_TABLE_LABELS.SCHEDULES,
    },
    {
      label: SUMMARY_TABLE_LABELS.PRE_SCHEDULES,
      tooltipInfo: {
        id: 'pre-schedules-info-tooltip',
        message: CUSTOM_PACING_INFO_TOOLTIPS.PRE_SCHEDULES,
        iconDataTestId: 'pre-schedules-info-icon',
      },
    },
    ...summary.windows.map((window) => ({
      label: `${SUMMARY_TABLE_LABELS.N_TH_PACING_SCHEDULE} ${window.id}`,
      tooltipInfo: {
        id: `pacing-schedule-${window.id}-info-tooltip`,
        message: CUSTOM_PACING_INFO_TOOLTIPS.PACING_SCHEDULE,
        iconDataTestId: `pacing-schedule-${window.id}-info-icon`,
      },
    })),
    {
      label: SUMMARY_TABLE_LABELS.POST_SCHEDULES,
      tooltipInfo: {
        id: 'post-schedules-info-tooltip',
        message: CUSTOM_PACING_INFO_TOOLTIPS.POST_SCHEDULES,
        iconDataTestId: 'post-schedules-info-icon',
      },
    },
  ];

  const pacingRow: SummaryTableData[] = [
    {
      value: SUMMARY_TABLE_LABELS.PACING,
      tooltipInfo: {
        id: 'percent-pacing-info-tooltip',
        message: CUSTOM_PACING_INFO_TOOLTIPS.PERCENT_PACING,
        iconDataTestId: 'percent-pacing-info-icon',
      },
    },
    { value: `${summary.preSchedule.pacing}%` },
    ...summary.windows.map((w) => ({ value: `${w.pacing}%` })),
    { value: `${summary.postSchedule.pacing}%` },
  ];

  const impressionRow: SummaryTableData[] = [
    {
      value: SUMMARY_TABLE_LABELS.TOTAL_IMPRESSION,
      tooltipInfo: {
        id: 'total-impressions-info-tooltip',
        message: CUSTOM_PACING_INFO_TOOLTIPS.TOTAL_IMPRESSIONS,
        iconDataTestId: 'total-impressions-info-icon',
      },
    },
    { value: formatNumber(summary.preSchedule.totalImpressions) },
    ...summary.windows.map((w) => ({ value: formatNumber(w.totalImpressions) })),
    { value: formatNumber(summary.postSchedule.totalImpressions) },
  ];

  return {
    pacingImpressionHeaders,
    pacingImpressionData: [pacingRow, impressionRow],
  };
};

export const mapSummarizationToPreview = (summary: SummaryMetrics): SummaryTableData[][] => {
  return [
    [
      {
        value: SUMMARY_TABLE_LABELS.LINES_SCHEDULED,
        tooltipInfo: {
          id: 'lines-schedules-info-tooltip',
          message: CUSTOM_PACING_INFO_TOOLTIPS.LINES_SCHEDULED,
          iconDataTestId: 'lines-scheduled-info-icon',
        },
      },
      { value: formatNumber(summary.linesScheduled) },
    ],
    [
      {
        value: SUMMARY_TABLE_LABELS.ESTIMATED_WINDOWS_SHIFT,
        tooltipInfo: {
          id: 'true-estimated-window-shift-info-tooltip',
          message: CUSTOM_PACING_INFO_TOOLTIPS.TRUE_ESTIMATED_WINDOW_SHIFT,
          iconDataTestId: 'true-estimated-window-shift-info-icon',
        },
      },
      { value: formatNumber(summary.trueEstimatedWindowsShift) },
    ],
    [
      {
        value: SUMMARY_TABLE_LABELS.DIFFERENCE_OVER_STRAIGHTLINE,
        tooltipInfo: {
          id: 'difference-over-straighline-daily-info-tooltip',
          message: CUSTOM_PACING_INFO_TOOLTIPS.DIFFERENCE_OVER_STRAIGHTLINE_DAILY,
          iconDataTestId: 'difference-over-straighline-daily-info-icon',
        },
      },
      { value: formatNumber(summary.differenceOverStraightlineDaily) },
    ],
  ];
};
